<script setup>
// 真实 Google AdSense 广告位 + 自有产品兜底。
// 谷歌广告填充 → 显示广告;未填充(dev 域名 / 无库存 / 被拦截)→ 显示 SharkFoto 自有产品推荐,
// 既保留站位又不浪费空间。检测:观察 <ins> 的 data-ad-status + 超时兜底(status 可能一直为 null)。
// variant: 'sky' 侧栏 300×600(slot 2809283864) | 'leaderboard' 横幅 728×90(slot 4476204231)。
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";

const props = defineProps({
  variant: { type: String, default: "sky" }, // 'sky' | 'leaderboard'
  label: { type: String, default: "" },
  // 阶段感知重载 key：变化时重挂内层 <Adsense> 重新 push 一次广告。
  reloadKey: { type: String, default: "" },
});
const route = useRoute();
const isLead = computed(() => props.variant === "leaderboard");

// 广告「形状」约束到设计族,修复填充广告高度不对(nuxt#42):
//  · leaderboard → format="horizontal"(横幅族 728×90/468×60/320×100/320×50…),AdSense 绝不会挑 250px 矩形;保留响应式,
//    移动端(leaderboard 唯一展示处)自动收成 320×50/100、不溢出窄屏。sky → format="vertical"(竖版 300×600/160×600,仅桌面)。
//  · 高度不用 `height !important` 硬锁 —— 那样 + 容器 overflow:hidden = 裁剪已投放广告,违反 AdSense Modifications 政策
//    (且 Similar-sized/Ad size optimization 默认开,固定单元也可能被投更高创意)。改用 min-height 预留设计高度(零 CLS)+
//    filled 态放开 overflow,即便被投更高创意也完整显示、绝不裁剪。像素级精确 90/600 需 Jack 在后台关掉那两项优化。
const adStyleFixed = computed(() =>
  isLead.value
    ? "display:block;width:100%;max-width:728px;min-height:90px;margin:0 auto"
    : "display:block;width:300px;min-height:600px"
);

// 兜底创意图放在 R2/CDN(/free-tools/assets/),按环境取 cdnUrl(dev/test=R2 公共域,prod=cdn.sharkfoto.com)。
// 换图只换 R2 文件;换产品/文案/版式只动下面 PROMOS。
const cdnUrl = useRuntimeConfig().public.cdnUrl;

// 两个自有产品(各含文案 + R2 图 + 版式)。layout:'shot'=单图 showcase | 'ba'=原图|抠像 before/after。trust 仅 sky 渲。
const PRODUCTS = {
  generator: {
    title: "Turn text into stunning images",
    desc: "Generate art, photos & designs from a single prompt — powered by AI.",
    cta: "Try AI Image Generator",
    to: "/ai/image/text-to-image/",
    img: "/free-tools/assets/promo-image-generator.webp",
    layout: "shot",
    trust: "Loved by 2M+ creators",
  },
  bgremover: {
    title: "Remove backgrounds in one click",
    desc: "AI background remover — clean cutouts, zero manual work.",
    cta: "Try Background Remover",
    to: "/ai/image/background-remover/",
    img: "/free-tools/assets/promo-background-remover.webp",
    imgSky: "/free-tools/assets/promo-background-remover-vertical.webp", // 竖版:sky(300×600)用;横版 before/after 塞竖槽会裁(leaderboard 仍用上面的横版 img)
    layout: "ba",
    trust: "Loved by 2M+ creators",
  },
};
// 两套轮换:每次整页加载「严格交替」一套(A↔B),sky / leaderboard 始终各展示不同产品(位置互换,两个产品都曝光)。
// 交替/随机必须在「客户端 onMounted」里做 —— 不能放 useState init,否则会在 SSR 烤进 HTML 被 CF Pages 缓存、轮换被冻死。
// localStorage('ftPromoRot') 记上次值、本次翻面;首访随机(均衡新访客首屏)。useState 仅作两实例间共享协调(同 key)。
const SETS = [
  { sky: "generator", lead: "bgremover" },
  { sky: "bgremover", lead: "generator" },
];
const rotation = useState("ftPromoRotation", () => 0);
const rotPicked = useState("ftPromoPicked", () => false);
const promo = computed(() => {
  const set = SETS[rotation.value] || SETS[0];
  return PRODUCTS[isLead.value ? set.lead : set.sky];
});
// sky 槽(!isLead)优先用产品的竖版图 imgSky(如有);leaderboard(横)用横版 img。
const promoImg = computed(() => {
  const p = promo.value;
  return cdnUrl + (!isLead.value && p.imgSky ? p.imgSky : p.img);
});

// 'pending'(等广告) | 'filled'(谷歌填充,显示广告) | 'promo'(未填充,显示自有推荐)
const state = ref("pending");
// 兜底图加载失败(prod CDN 未就位 / R2 抖动 / 拦截器挡 CDN 域)→ 隐藏图,优雅退回纯文字推荐(不出破图)。
const imgFailed = ref(false);
const slotEl = ref(null);
let observer = null;
let timer = null;

function readStatus() {
  const ins = slotEl.value?.querySelector("ins.adsbygoogle");
  return ins ? ins.getAttribute("data-ad-status") : null;
}
function evaluate() {
  const s = readStatus();
  // 只有 filled(谷歌真投了广告)才显示广告;其余任何已决议状态(unfilled / unfill-optimized / …)
  // 一律回退自家 promo —— 否则空白广告框会露出白框/细条。(nuxt#39 回归修复)
  if (s === "filled") state.value = "filled";
  else if (s) state.value = "promo";
}
function startWatch() {
  state.value = "pending"; // 先给谷歌广告机会
  imgFailed.value = false; // 重挂时重试兜底图
  if (observer) { observer.disconnect(); observer = null; }
  if (timer) { clearTimeout(timer); timer = null; }
  const ins = slotEl.value?.querySelector("ins.adsbygoogle");
  if (ins) {
    observer = new MutationObserver(evaluate);
    observer.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
    evaluate();
  }
  // 兜底:超时仍未 filled(unfilled / 脚本被拦 / 无 status)→ 显示自有产品
  timer = setTimeout(() => { if (readStatus() !== "filled") state.value = "promo"; }, 2500);
}

onMounted(() => {
  // 客户端选轮换:首个挂载的 AdSlot 选定,另一个复用(rotPicked 协调)。
  // 严格交替:读 localStorage 上次值 → 翻面(A↔B);首访(无记录/不可用)随机,保证新访客首屏均衡。
  if (!rotPicked.value) {
    let next;
    try {
      const last = localStorage.getItem("ftPromoRot");
      next = last === "0" || last === "1" ? 1 - Number(last) : (Math.random() < 0.5 ? 1 : 0);
      localStorage.setItem("ftPromoRot", String(next));
    } catch (e) {
      next = Math.random() < 0.5 ? 1 : 0; // localStorage 不可用(隐私模式等)→ 退回随机
    }
    rotation.value = next;
    rotPicked.value = true;
  }
  nextTick(startWatch);
});
watch(() => props.reloadKey || route.path, () => nextTick(startWatch));
onBeforeUnmount(() => { observer?.disconnect(); if (timer) clearTimeout(timer); });
</script>

<template>
  <div
    ref="slotEl"
    class="ad-slot"
    :class="[
      isLead ? 'ad-leaderboard' : 'ad-sky',
      state === 'promo' ? 'is-promo' : '',
      state === 'filled' ? 'is-filled' : '',
    ]"
  >
    <ClientOnly>
      <Adsense
        v-show="state !== 'promo'"
        :key="reloadKey || route.path"
        :ad-style="adStyleFixed"
        :format="isLead ? 'horizontal' : 'vertical'"
        client-id="ca-pub-6439841372484008"
        :slot-id="isLead ? '4476204231' : '2809283864'"
        :full-width-responsive="isLead ? 'true' : 'false'"
      />
      <NuxtLink
        v-if="state === 'promo'"
        class="ad-promo"
        :class="isLead ? 'promo-lead' : 'promo-sky'"
        :to="promo.to"
      >
        <span class="glow" />
        <span v-if="promo.layout === 'shot' && !imgFailed" class="pm-shot">
          <img :src="promoImg" alt="" loading="eager" decoding="async" @error="imgFailed = true" />
          <svg class="spark" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.6 4.8L18 8l-4.4 1.2L12 14l-1.6-4.8L6 8l4.4-1.2z" /></svg>
        </span>
        <span v-else-if="!imgFailed" class="ba">
          <img :src="promoImg" alt="" loading="eager" decoding="async" @error="imgFailed = true" />
        </span>
        <span class="pm-text">
          <span class="pm-title">{{ promo.title }}</span>
          <span class="pm-desc">{{ promo.desc }}</span>
        </span>
        <span class="pm-cta">{{ promo.cta }}<i>→</i></span>
        <span v-if="!isLead && promo.trust" class="pm-trust">
          <span class="av"><span /><span /><span /></span>{{ promo.trust }}
        </span>
      </NuxtLink>
    </ClientOnly>
  </div>
</template>

<style>
/* 广告 <ins> 别被 .ad-slot 的 flex(align-items:center)压成 0 宽 —— 否则 AdSense push 时量到
   availableWidth=0、报 "No slot size" 而永不渲染(status 一直 null)→ 永远回退自家 promo。
   宽度由 ad-style 的显式 width(100%/300px,非 auto)保证 >0,AdSense 能初始化;不再用 align-self:stretch
   (它会盖掉容器 align-items:center 的居中,让 728 横幅在宽列里贴左)。高度由 ad-style 的 min-height 预留 + filled 态放开。 */
.ft-scope .ad-slot ins.adsbygoogle { display: block; overflow: hidden; }
/* AdSense 未填充时会给广告位「容器」写 inline height:0 折叠它(prod 实测:fwr=false 的 sky 被折叠成 0、promo 塌成 42px;
   fwr=true 的 leaderboard 不会)。inline height 压过我们 CSS 的 height,故用 min-height 兜住 —— CSS 规范里 min-height 胜过
   (含 inline 的)height,容器不会被折叠,promo/预留高度稳住。dev 域名不投广告碰不到,只在 prod 复现,所以务必上 prod 复验。 */
.ft-scope .ad-slot.ad-sky { min-height: 600px; }
.ft-scope .ad-slot.ad-leaderboard { min-height: 90px; }
/* 自有推荐时:这不是广告 —— 去掉 "Advertisement" 标签 + 虚线占位底。 */
.ft-scope .ad-slot.is-promo::before { content: none; }
.ft-scope .ad-slot.is-promo { border: none; background: none; padding: 0; }
/* 关键:promo 兜底时强制隐藏广告 ins。v-show 会被 AdSense 处理后写在 ins 上的 inline display 盖掉 →
   空白广告 iframe 会露成白框。用 !important 直接压掉,只显示自家 promo(修白框回归)。 */
.ft-scope .ad-slot.is-promo ins.adsbygoogle { display: none !important; }
/* 真广告填充时:去掉虚线占位框,让广告干净显示(顶部 Advertisement 合规标签保留)。 */
.ft-scope .ad-slot.is-filled { border-color: transparent; background: none; }
/* 填充态:放开容器/ins 的高度锁与 overflow —— 万一 Google 投了比设计更高的相近尺寸创意,让它完整显示、绝不裁剪(合规)。
   min-height 仍预留设计高度(90/600)保证零 CLS;正常投放就是横幅/竖版、刚好等高,容器不会变高。 */
.ft-scope .ad-slot.ad-leaderboard.is-filled { height: auto; min-height: 90px; overflow: visible; }
.ft-scope .ad-slot.ad-sky.is-filled { height: auto; min-height: 600px; overflow: visible; }
.ft-scope .ad-slot.is-filled ins.adsbygoogle { overflow: visible; }
</style>

<style scoped>
.ad-promo {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  text-decoration: none;
  border-radius: 14px;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid var(--line-strong);
  color: var(--tx);
  background: var(--bg-2);
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}
.ad-promo:hover {
  border-color: rgba(139, 92, 246, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 18px 40px -18px rgba(139, 92, 246, 0.5);
}
.glow { position: absolute; inset: 0; z-index: -1; pointer-events: none; }

.pm-title { color: #fff; font-family: var(--font-h); font-weight: 700; line-height: 1.14; letter-spacing: -0.02em; }
.pm-desc { color: var(--tx-dim); }
.pm-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, var(--brand), var(--brand-2));
  border-radius: 10px;
  box-shadow: 0 8px 20px -8px rgba(139, 92, 246, 0.7);
}
.pm-cta i { font-style: normal; transition: transform 0.2s ease; }
.ad-promo:hover .pm-cta i { transform: translateX(3px); }
.pm-trust { display: flex; align-items: center; gap: 7px; color: var(--tx-faint, rgba(244, 246, 250, 0.42)); font-size: 11.5px; font-weight: 600; }
.pm-trust .av { display: flex; }
.pm-trust .av span { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid var(--bg-2); margin-left: -6px; }
.pm-trust .av span:nth-child(1) { margin-left: 0; background: #ff6f9c; }
.pm-trust .av span:nth-child(2) { background: #4ea2ff; }
.pm-trust .av span:nth-child(3) { background: #34d399; }

/* 单图 showcase(generator):整图 + 右上 sparkle 角标 */
.pm-shot { position: relative; border-radius: 10px; overflow: hidden; flex: none; border: 1px solid rgba(255, 255, 255, 0.08); background: #15171f; }
.pm-shot img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pm-shot::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, transparent 62%, rgba(0, 0, 0, 0.2)); }
.pm-shot .spark { position: absolute; top: 9px; right: 9px; width: 16px; height: 16px; color: #fff; opacity: 0.95; filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.6)); z-index: 1; }

/* before/after 对照(bg-remover):原图 | 透明抠像,棋盘格透明底 */
.ba { position: relative; border-radius: 10px; overflow: hidden; flex: none; border: 1px solid rgba(255, 255, 255, 0.08); background: repeating-conic-gradient(#171922 0% 25%, #11131a 0% 50%) 50% / 16px 16px; }
.ba img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* 侧栏 300×600:纵向,图主导 */
.promo-sky { flex-direction: column; padding: 20px; }
.promo-sky .glow {
  background:
    radial-gradient(80% 38% at 78% 0%, rgba(139, 92, 246, 0.42), transparent 62%),
    radial-gradient(70% 30% at 10% 100%, rgba(34, 211, 238, 0.18), transparent 60%),
    linear-gradient(165deg, #1c1638, var(--bg-2));
}
/* 图自适应填满文案上方剩余空间(flex:1)→ CTA + trust 在 600px 内永不裁切(不再用固定 aspect/height 撑爆) */
.promo-sky .pm-shot, .promo-sky .ba { flex: 1 1 0; width: 100%; min-height: 120px; margin-bottom: 18px; }
.promo-sky .pm-text { display: flex; flex-direction: column; gap: 10px; }
.promo-sky .pm-title { font-size: 24px; }
.promo-sky .pm-desc { font-size: 13.5px; line-height: 1.55; }
.promo-sky .pm-cta { width: 100%; padding: 14px; font-size: 14px; margin-top: 16px; }
.promo-sky .pm-trust { margin-top: 14px; justify-content: center; }

/* 横幅 728×90:横向紧凑;锁 728 居中 → 宽容器(如 download 页)里不拉伸贴边 */
.promo-lead { flex-direction: row; align-items: center; gap: 18px; padding: 0 16px; max-width: 728px; margin: 0 auto; }
.promo-lead .glow {
  background:
    radial-gradient(40% 130% at 0% 50%, rgba(139, 92, 246, 0.4), transparent 60%),
    radial-gradient(40% 130% at 100% 50%, rgba(34, 211, 238, 0.16), transparent 60%),
    linear-gradient(100deg, #1b1535, var(--bg-2));
}
.promo-lead .pm-shot { width: 150px; height: 74px; }
/* 横幅太小:图放大到右上象限只露单人特写,更有冲击 */
.promo-lead .pm-shot img { position: absolute; width: 200%; height: auto; top: 0; right: 0; left: auto; object-fit: unset; }
.promo-lead .pm-shot .spark { display: none; }
.promo-lead .ba { width: 150px; height: 74px; }
.promo-lead .pm-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.promo-lead .pm-title { font-size: 17px; }
.promo-lead .pm-desc { font-size: 12.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.promo-lead .pm-cta { flex: none; padding: 11px 18px; font-size: 13px; white-space: nowrap; }
.promo-lead .pm-spacer, .promo-lead .pm-trust { display: none; }
</style>
